import { useCallback, useEffect, useRef, useState ,memo} from "react";

import { Trans, msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { patientIntakeApi, buildFormPayload } from "@/lib/patientIntakeApi";
import type { IntakeData } from "@/lib/types";
import {
  ClipboardCheck,
  UserCheck,
  FileText,
  CheckCircle,
  Upload,
  Shield,
  ChevronLeft,
  ChevronRight,
  Camera,
  Brain,
  HelpCircle,
} from "lucide-react";
 
import { useSearchParams } from "react-router-dom";

// Import simplified step components
import WelcomeStep from "../components/intake/WelcomeStep";
import MedicalProcedureStep from "../components/intake/MedicalProcedureStep";
import UploadsStep from "../components/intake/UploadsStep";
import ReviewConsentStep from "../components/intake/ReviewConsentStep";
import VerifyEmailStep from "../components/intake/VerifyEmailStep";
import SuccessStep from "../components/intake/SuccessStep";
import { useTranslation } from "react-i18next";


 
import DynamicQuestionTree from "../components/intake/dentist/DynamicQuestionTree";
import PhotoCaptureStep from "../components/intake/dentist/PhotoCaptureStep";
import AdditionalPhotosStep from "../components/intake/dentist/AdditionalPhotosStep";
import ComprehensiveDentalAnalysis from "../components/intake/dentist/ComprehensiveDentalAnalysis";
import ReviewStep from "../components/intake/dentist/ReviewStep";
import OTPVerificationStep from "../components/intake/dentist/OTPVerificationStep";
import SinglePhotoRetake from "../components/intake/dentist/SinglePhotoRetake";
 
const MemoizedDynamicQuestionTree = memo(DynamicQuestionTree);
const MemoizedPhotoCaptureStep = memo(PhotoCaptureStep);
const MemoizedAdditionalPhotosStep = memo(AdditionalPhotosStep);
const MemoizedComprehensiveDentalAnalysis = memo(ComprehensiveDentalAnalysis);
const MemoizedReviewStep = memo(ReviewStep);
const MemoizedOTPVerificationStep = memo(OTPVerificationStep);
const MemoizedSuccessStep = memo(SuccessStep);


const STEP_SUBHEADINGS = [
  msg`Let's get started with your information`,
  msg`Tell us about your medical needs`,
  msg`Upload your documents`,
  msg`Review and submit your intake`,
  msg`Verify your email address`,
  msg`All done! Welcome to Jema Services`,
];

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const sanitizeIntakeUpdates = (updates: Partial<IntakeData>) => {
  const sanitized = {} as Record<
    keyof IntakeData,
    IntakeData[keyof IntakeData]
  >;
  (
    Object.entries(updates) as Array<
      [keyof IntakeData, IntakeData[keyof IntakeData]]
    >
  ).forEach(([key, value]) => {
    if (typeof value === "string") {
      const trimmed = value.trim();
      sanitized[key] = (
        key === "email" ? trimmed.toLowerCase() : trimmed
      ) as IntakeData[keyof IntakeData];
      return;
    }
    if (Array.isArray(value)) {
      sanitized[key] = value as IntakeData[keyof IntakeData];
      return;
    }
    if (value !== undefined) {
      sanitized[key] = value;
    }
  });

  return sanitized as Partial<IntakeData>;
};

const hasValidIdentity = (data: IntakeData) => {
  const firstName = data.firstName?.trim();
  const lastName = data.lastName?.trim();
  const email = data.email?.trim();
  const phone = data.phone?.trim();
  const country = data.country?.trim();

  return (
    Boolean(firstName) &&
    Boolean(lastName) &&
    Boolean(phone) &&
    Boolean(country) &&
    Boolean(email && emailRegex.test(email))
  );
};



export default function Index() {
  const { i18n } = useLingui();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const contentRef = useRef<HTMLElement | null>(null);
  const didMountRef = useRef(false);
  const hasHydratedHashRef = useRef(false);
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);


  const [intakeData, setIntakeData] = useState<IntakeData>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    country: "",
    preferredLanguage: "en",
    procedures: "",
    dateOfBirth: "",
    preExistingConditions: "",
    currentMedications: "",
    allergies: "",
    majorSurgeries: "",
    smokingHabits: "",
    dentalProcedureType: "",
    dentalDiscomfort: "",
    uploads: {},
    consentGiven: false,
    emailVerified: false,
  });

  const STEPS = [
    {
      id: "welcome",
      hash: "welcome-step",
      title: msg`Personal Info`,
      icon: UserCheck,
    },
    {
      id: "medical",
      hash: "medical-step",
      title: msg`Medical Info`,
      icon: FileText,
    },
    ...(intakeData.procedures === "dentistry"  
    ? [ 
      { 
        id: "detailed",
        hash: "detailed",
        title:  msg`Detailed Questions`,
        icon: HelpCircle
      },
      { id: "photos",
        title: msg `Your Photos`,
        hash: "your-photos",
        icon: Camera
      },
      { id: "additional-photos",
         title: msg `Additional Photos`,
          hash: "additional-photos",
          icon: Upload
         },
      { id: "analysis",
         title: msg `Full Analysis`,
         hash: "full-analysis",
          icon: Brain
         },
      { 
        id: "review",
         title: msg `Review & Submit`,
          icon: ClipboardCheck,
          hash: "review-submit",
         },
      { 
        id: "verification",
         title: msg`Email Verification`,
          icon: Shield,
          hash: "email-verification",
         },
      { 
        id: "success",
         title: msg `All Done!`, 
         icon: CheckCircle,  
         hash: "all-done", },
    ] : [
      {
        id: "uploads",
        hash: "uploads-step",
        title: msg`Upload Documents`,
        icon: Upload,
      },
      {
        id: "review",
        hash: "review-step",
        title: msg`Review & Submit`,
        icon: ClipboardCheck,
      },
      {
        id: "verification",
        hash: "verification-step",
        title: msg`Email Verification`,
        icon: Shield,
      },
      {
        id: "success",
        hash: "success",
        title: msg`All Done!`,
        icon: CheckCircle,
      },  
    ]),
  ];

  const intakeDataRef = useRef<IntakeData>(intakeData);
  const draftSyncTimeoutRef = useRef<number | null>(null);
  const draftSyncPromiseRef = useRef<Promise<void> | null>(null);
  const lastSyncedSignatureRef = useRef<string | null>(null);

  const [isDesktop, setIsDesktop] = useState<boolean>(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return window.innerWidth >= 768;
  });

  useEffect(() => {
    intakeDataRef.current = intakeData;
  }, [intakeData]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const scrollToTop = useCallback((behavior: ScrollBehavior = "smooth") => {
    const scrollContainers = () => {
      const container = contentRef.current;
      if (container) {
        if (typeof container.scrollTo === "function") {
          container.scrollTo({ top: 0, left: 0, behavior });
        } else {
          container.scrollTop = 0;
          container.scrollLeft = 0;
        }
      }
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, left: 0, behavior });
        window.scrollTo(0, 0);
        const globalTargets: Array<HTMLElement | null> = [
          (document.scrollingElement as HTMLElement | null) ?? null,
          document.documentElement,
          document.body,
        ];
        globalTargets.forEach((target) => {
          if (!target) {
            return;
          }
          if (typeof target.scrollTo === "function") {
            try {
              target.scrollTo({ top: 0, left: 0, behavior });
            } catch {
              target.scrollTop = 0;
              target.scrollLeft = 0;
            }
          }
          target.scrollTop = 0;
          target.scrollLeft = 0;
        });
      }

      const firstChild = container?.firstElementChild as HTMLElement | null;
      if (firstChild?.scrollIntoView) {
        firstChild.scrollIntoView({ behavior, block: "start" });
      }
    };

    scrollContainers();

    if (typeof window !== "undefined") {
      window.requestAnimationFrame(() => {
        scrollContainers();
      });
      window.setTimeout(() => {
        scrollContainers();
      }, 150);
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const initialHash = window.location.hash.replace("#", "");
    if (!initialHash) {
      return;
    }

    const matchedIndex = STEPS.findIndex(
      (step) => (step.hash ?? step.id) === initialHash
    );

    if (matchedIndex >= 0) {
      setCurrentStep(matchedIndex);
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (!hasHydratedHashRef.current) {
      hasHydratedHashRef.current = true;
      if (window.location.hash) {
        return;
      }
    }

    const step = STEPS[currentStep];
    if (!step) {
      return;
    }

    const hashValue = step.hash ?? step.id ?? "";
    const desiredHash = hashValue ? `#${hashValue}` : "";

    if (window.location.hash === desiredHash) {
      return;
    }

    const { pathname, search } = window.location;
    window.history.replaceState(
      null,
      "",
      `${pathname}${search}${desiredHash}`
    );
  }, [currentStep]);

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    scrollToTop();
  }, [currentStep, scrollToTop]);

  // Handle step array changes when procedures change
  useEffect(() => {
    // Ensure currentStep is within bounds when STEPS array changes
    if (currentStep >= STEPS.length) {
      setCurrentStep(Math.max(0, STEPS.length - 1));
    }
  }, [STEPS.length, currentStep]);

  useEffect(() => {
    stepRefs.current[currentStep]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  }, [currentStep]);

  const performDraftSync = useCallback(async () => {
    const initial = intakeDataRef.current;
    const initialFormUid = initial.formUid;
    if (!initialFormUid) {
      return;
    }

    if (draftSyncPromiseRef.current) {
      try {
        await draftSyncPromiseRef.current;
      } catch {
        // Previous sync errors are already logged
      }
    }

    const latest = intakeDataRef.current;
    if (!latest.formUid) {
      return;
    }

    if (!hasValidIdentity(latest)) {
      return;
    }

    const payload = buildFormPayload(latest);
    const signature = JSON.stringify(payload);
    if (lastSyncedSignatureRef.current === signature) {
      return;
    }

    const syncPromise = (async () => {
      try {
        await patientIntakeApi.updateIntake(latest.formUid!, latest);
        lastSyncedSignatureRef.current = signature;
      } catch (error) {
        console.error("Failed to sync intake draft", error);
      }
    })();

    draftSyncPromiseRef.current = syncPromise;

    try {
      await syncPromise;
    } finally {
      if (draftSyncPromiseRef.current === syncPromise) {
        draftSyncPromiseRef.current = null;
      }
    }
  }, []);

  const scheduleDraftSync = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (!intakeDataRef.current.formUid) {
      return;
    }

    if (draftSyncTimeoutRef.current) {
      window.clearTimeout(draftSyncTimeoutRef.current);
    }

    draftSyncTimeoutRef.current = window.setTimeout(() => {
      draftSyncTimeoutRef.current = null;
      void performDraftSync();
    }, 800);
  }, [performDraftSync]);

  const flushDraftSync = useCallback(async () => {
    if (typeof window !== "undefined" && draftSyncTimeoutRef.current) {
      window.clearTimeout(draftSyncTimeoutRef.current);
      draftSyncTimeoutRef.current = null;
    }

    await performDraftSync();

    if (draftSyncPromiseRef.current) {
      try {
        await draftSyncPromiseRef.current;
      } catch {
        // Error already logged during performDraftSync
      }
    }
  }, [performDraftSync]);

  useEffect(() => {
    return () => {
      if (typeof window !== "undefined" && draftSyncTimeoutRef.current) {
        window.clearTimeout(draftSyncTimeoutRef.current);
        draftSyncTimeoutRef.current = null;
      }
      void flushDraftSync();
    };
  }, [flushDraftSync]);

  const updateIntakeData = useCallback(
    (data: Partial<IntakeData>) => {
      const sanitized = sanitizeIntakeUpdates(data);
      setIntakeData((prev) => {
        const next = { ...prev, ...sanitized };
        intakeDataRef.current = next;
        if (next.formUid) {
          scheduleDraftSync();
        }
        return next;
      });
    },
    [setIntakeData, scheduleDraftSync]
  );

  const verifiedParam = searchParams.get("verified");
  const queryFormUid = searchParams.get("formUid");
  const queryReferenceUid = searchParams.get("referenceUid");

  useEffect(() => {
    if (verifiedParam === "1" && queryFormUid) {
      updateIntakeData({
        formUid: queryFormUid,
        referenceUid: queryReferenceUid ?? undefined,
        emailVerified: true,
      });
      setCurrentStep(5);
      setSearchParams({}, { replace: true });
    }
  }, [verifiedParam, queryFormUid, queryReferenceUid, updateIntakeData, setSearchParams]);

  const ensureDraftForm = useCallback(
    async (identityUpdates: Partial<IntakeData>) => {
      updateIntakeData(identityUpdates);

      const current = intakeDataRef.current;
      const existingFormUid = current.formUid;

      if (existingFormUid) {
        return;
      }

      const email = (current.email || "").trim().toLowerCase();
      if (!email) {
        throw new Error("Email is required to create your intake form.");
      }

      try {
        const payload: IntakeData = {
          ...intakeDataRef.current,
          email,
          uploads: current.uploads || {},
          consentGiven: current.consentGiven ?? false,
          emailVerified: current.emailVerified ?? false,
        };

        const response = await patientIntakeApi.submitIntake(payload);
        const formUid = response.data?.formUid;
        const referenceUid = response.data?.referenceUid;

        if (!formUid) {
          throw new Error("Failed to create intake form.");
        }

        updateIntakeData({ formUid, referenceUid });
        if (typeof window !== "undefined" && draftSyncTimeoutRef.current) {
          window.clearTimeout(draftSyncTimeoutRef.current);
          draftSyncTimeoutRef.current = null;
        }
        lastSyncedSignatureRef.current = JSON.stringify(
          buildFormPayload(intakeDataRef.current)
        );
        console.log("✅ Created new intake form draft:", formUid);
      } catch (creationError) {
        console.error("Error creating intake form:", creationError);
        const message =
          creationError instanceof Error
            ? creationError.message
            : "We could not create your intake form. Please try again.";
        throw new Error(message);
      }
    },
    [updateIntakeData]
  );

  const nextStep = async () => {
    if (currentStep < STEPS.length - 1) {
      const nextStepIndex = currentStep + 1;
      const nextStepData = STEPS[nextStepIndex];

      // Create form when transitioning to review step - after all data is collected
      // Check by step ID instead of index to handle different flow lengths
      if (nextStepData?.id === "review" && !intakeDataRef.current.formUid) {
        try {
          // Validate required fields before creating form
          if (!hasValidIdentity(intakeDataRef.current)) {
            throw new Error("Please complete all required fields before proceeding.");
          }

          const response = await patientIntakeApi.submitIntake(
            intakeDataRef.current
          );
          const formUid = response.data?.formUid;
          const referenceUid = response.data?.referenceUid;

          if (formUid) {
            updateIntakeData({ formUid, referenceUid });
            if (typeof window !== "undefined" && draftSyncTimeoutRef.current) {
              window.clearTimeout(draftSyncTimeoutRef.current);
              draftSyncTimeoutRef.current = null;
            }
            lastSyncedSignatureRef.current = JSON.stringify(
              buildFormPayload(intakeDataRef.current)
            );
          } else {
            throw new Error("Failed to create intake form");
          }
        } catch (error) {
          console.error("Error creating intake form:", error);
          const errorMessage = error instanceof Error 
            ? error.message 
            : "There was an error creating your intake form. Please try again.";
          alert(errorMessage);
          return; // Don't proceed to next step
        }
      }

      scrollToTop("auto");
      setCurrentStep((prev) => Math.min(prev + 1, STEPS.length - 1));
      if (typeof window !== "undefined") {
        window.setTimeout(() => {
          scrollToTop("smooth");
        }, 50);
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      scrollToTop("auto");
      setCurrentStep((prev) => Math.max(prev - 1, 0));
      if (typeof window !== "undefined") {
        window.setTimeout(() => {
          scrollToTop("smooth");
        }, 50);
      }
    }
  };


  const skipStep = () => {
    scrollToTop("auto");
      setCurrentStep((prev) => Math.max(prev + 1, 0));
      if (typeof window !== "undefined") {
        window.setTimeout(() => {
          scrollToTop("smooth");
        }, 50);
      }
  };

  const [retakePhotoContext, setRetakePhotoContext] = useState<{
    photoId: string;
    mediaId?: string;
  } | null>(null);

  const handleRetakePhoto = (payload: {
    photoId: string;
    mediaId?: string;
  }) => {
    setRetakePhotoContext(payload);
  };

  const handleRetakeComplete = () => {
    setRetakePhotoContext(null);
    setCurrentStep(STEPS.findIndex((step) => step.id === "review"));
  };

  const handleRetakeCancel = () => {
    setRetakePhotoContext(null);
  };

  const handleOTPVerificationSuccess = () => {
    // Redirect to jema-manager will be handled in the OTP component
    nextStep(); // Go to success step
  };

  const handleVerificationSuccess = () => {
    nextStep(); // Go to success step
  };

  const submitIntake = async () => {
    setIsSubmitting(true);
    try {
      await flushDraftSync();

      const currentData = intakeDataRef.current;

      // Form is already created, just proceed to email verification
      if (currentData.formUid) {
        console.log(
          "✅ Finalizing intake form before verification",
          currentData.formUid
        );
        await patientIntakeApi.finalizeIntake(currentData.formUid);
        console.log("✅ Form finalized, proceeding to email verification");
        nextStep(); // Go to email verification step
      } else {
        throw new Error("Form ID not found. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting intake:", error);
      alert("There was an error submitting your intake. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const { t } = useTranslation();

  const safeStepIndex = Math.min(currentStep, STEPS.length - 1);
  const currentStepData = STEPS[safeStepIndex];
  const currentStepTitle = currentStepData ? i18n._(currentStepData.title) : "";
  const trackableSteps = STEPS.slice(0, -1);
  const totalStepsToShow = trackableSteps.length;
  const lastTrackableIndex = Math.max(totalStepsToShow - 1, 0);
  const clampedStepIndex = Math.min(safeStepIndex, lastTrackableIndex);
  const progressFraction =
    lastTrackableIndex <= 0 ? 1 : clampedStepIndex / lastTrackableIndex;
  const progressPercent = Math.round(progressFraction * 100);
  const completedIntakeLabel = t`Completed Intake`;
  const activeStepTitle =
    safeStepIndex < totalStepsToShow 
      ? currentStepTitle
      : completedIntakeLabel;

  const displayStepNumber = safeStepIndex + 1;
  const visibleStepNumber = Math.min(displayStepNumber, totalStepsToShow);
  const activeStepSubtitleDescriptor =
    STEP_SUBHEADINGS[currentStep] ??
    STEP_SUBHEADINGS[STEP_SUBHEADINGS.length - 1];
  const activeStepSubtitle = i18n._(activeStepSubtitleDescriptor);

 

  const dentalItems = [
    (
      <MemoizedDynamicQuestionTree
        data={intakeData}
        onUpdate={updateIntakeData}
        onNext={nextStep}
        onBack={prevStep}
      />
    ),
    (
      <MemoizedPhotoCaptureStep
        data={intakeData}
        onUpdate={updateIntakeData}
        onNext={nextStep}
      />
    ),
    (
      <MemoizedAdditionalPhotosStep
        data={intakeData}
        onUpdate={updateIntakeData}
        onNext={nextStep}
        onBack={prevStep}
      />
    ),
    (
      <MemoizedComprehensiveDentalAnalysis
        data={intakeData}
        onUpdate={updateIntakeData}
        onNext={nextStep}
      />
    ),
    (
      <MemoizedReviewStep
        data={intakeData}
        onUpdate={updateIntakeData}
        onSubmit={submitIntake}
        isSubmitting={isSubmitting}
        onRetakePhoto={handleRetakePhoto}
        onBack={prevStep}
      />
    ),
    (
      <MemoizedOTPVerificationStep
        data={intakeData}
        onUpdate={updateIntakeData}
        onVerificationSuccess={handleOTPVerificationSuccess}
        onBack={prevStep}
        formUid={intakeData.formUid}
        referenceUid={intakeData.referenceUid}
      />
    ),
 

    <MemoizedSuccessStep data={intakeData} />
  ];


 
  const items = [
    (
      <WelcomeStep
        data={intakeData}
        onUpdate={updateIntakeData}
        onEnsureDraft={ensureDraftForm}
        onNext={nextStep}
      />
    ),
    (
      <MedicalProcedureStep
        data={intakeData}
        onUpdate={updateIntakeData}
        onNext={nextStep}
        onBack={prevStep}
      />
    ),
    ...(intakeData.procedures === "dentistry"  
    ? dentalItems : [(
        <UploadsStep
          data={intakeData}
          onUpdate={updateIntakeData}
          onNext={nextStep}
          onBack={prevStep}
        />
      ),
      (
        <ReviewConsentStep
          data={intakeData}
          onUpdate={updateIntakeData}
          onSubmit={submitIntake}
          isSubmitting={isSubmitting}
          onBack={prevStep}
        />
      ),
      (
        <VerifyEmailStep
          data={intakeData}
          onUpdate={updateIntakeData}
          onVerificationSuccess={handleVerificationSuccess}
          onBack={prevStep}
          formUid={intakeData.formUid}
          referenceUid={intakeData.referenceUid}
        />
      ),
      <SuccessStep data={intakeData} />
    ]),
   
  ];




  if (retakePhotoContext) {
    return (
      <SinglePhotoRetake
        data={intakeData}
        photoId={retakePhotoContext.photoId}
        existingMediaId={retakePhotoContext.mediaId}
        photoTitle=""
        onUpdate={updateIntakeData}
        onComplete={handleRetakeComplete}
        onCancel={handleRetakeCancel}
      />
    );
  }
  
  const renderStep = () => {
    // Use direct array access to handle dynamic step changes
    if (currentStep >= 0 && currentStep < items.length) {
      return items[currentStep];
    }
    return null;
  };

  const stepContent = renderStep();





  return (
    <div className="min-h-screen bg-[#F5FAFF]">
      {isDesktop ? (
        <div className="flex h-screen bg-[#EEF7FC] overflow-hidden">
          <aside className="w-72 lg:w-80 flex-shrink-0 bg-gradient-to-br from-[#166B93] via-[#166B93] to-[#CEF12F] text-white flex flex-col shadow-lg h-full">
            <div className="flex-shrink-0 p-6 border-b border-white/20">
              <div className="flex flex-col items-center">
                <img
                  src="/jema services logo full.svg"
                  alt="Jema Services Logo"
                  className="object-contain brightness-0 invert"
                />
              </div>
              <div className="text-white">
                <h1 className="text-lg font-semibold text-white mb-1">
 
                  <Trans>Medical Care Without Borders</Trans>
                </h1>
                <h2 className="text-sm text-white/90 mb-4">
                  <Trans>Premium Medical Tourism in Colombia</Trans>
                </h2>
                <p className="text-xs text-white/80 leading-relaxed">
                  <Trans>
                    World-class medical care powered by expert physicians.
                    Personalized treatment plans and a journey worth taking.
                  </Trans>
 
                </p>
              </div>
            </div>
            {currentStep < STEPS.length - 1 ? (
              <nav className="flex-1 px-4 py-6 space-y-2 overflow-hidden">
                <h3 className="text-white font-medium text-xs mb-3 px-4 uppercase tracking-wider">
 
                  <Trans>Your Progress</Trans>
 
                </h3>
                <div className="space-y-2">
                  {STEPS.slice(0, -1).map((step, index) => {
                    const stepTitle = i18n._(step.title);
                    const StepIcon = step.icon;
                    const isActive = index === currentStep;
                    const isCompleted = index < currentStep;
                    return (
                      <div
                        key={step.id}
                        ref={(el) => {
                          if (el) stepRefs.current[index] = el;
                        }}
                        className={`flex items-center gap-4 p-3 mx-2 rounded-lg transition-all duration-300 cursor-default ${isActive ? "bg-white/25 shadow-md" : isCompleted ? "text-white/80 bg-white/10 hover:bg-white/15" : "text-white/60"}`}
                      >
                        <div
                          className={`w-8 h-8 rounded-md flex items-center justify-center text-sm font-bold flex-shrink-0 transition-all duration-300 ${isActive ? "bg-white text-[#166B93]" : isCompleted ? "bg-[#CEF12F] text-gray-900" : "bg-white/20 text-white/70"}`}
                        >
                          {isCompleted ? (
                            <CheckCircle className="w-5 h-5" />
                          ) : (
                            <StepIcon className="w-5 h-5" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-sm lg:text-base truncate">
 
                            {stepTitle}
 
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </nav>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <CheckCircle className="w-16 h-16 mx-auto text-white mb-4" />
                  <p className="mt-4 text-lg">
                    <Trans>Thank you!</Trans>
                  </p>
                </div>
              </div>
            )}
            <div className="flex-shrink-0 p-4 border-t border-white/20 text-white/60 text-xs text-center">
              <p>© 2024 Jema Services. All Rights Reserved.</p>
            </div>
          </aside>
          <div className="flex-1 flex flex-col min-h-0 h-full">
            <header className="flex-shrink-0 bg-white border-b border-gray-200">
              
              <div className="h-20 flex items-center justify-between px-6 lg:px-8">

                {currentStep < STEPS.length - 1 ? (
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-[#166B93]/10 text-[#166B93]">
                      <currentStepData.icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800 text-lg">
 
                        {currentStepTitle}
                      </h3>
                      <p className="text-sm text-gray-500">
                        <Trans id="desktop.progress">
                          Step {visibleStepNumber} of {totalStepsToShow}
                        </Trans>
 
                      </p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <h3 className="font-semibold text-gray-800 text-lg">
                      {currentStepTitle}
                    </h3>
                  </div>
                )}

                <div className="flex items-center gap-3">

                  {currentStep > 0 && currentStep < STEPS.length - 1 && (
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={prevStep}
                    className="flex items-center gap-2"
                  >
                    <ChevronLeft className="w-5 h-5" />
                    <Trans>Back</Trans>
                  </Button>
                )}
 

{currentStep === 2 && (
                    <Button
                    variant="outline"
                    size="lg"
                    onClick={skipStep}
                    className="items-center gap-1"
                  >
                     {t("Skip")}
                    <ChevronRight className="w-5 h-5" />  
                </Button> 
                )}
              </div>
              </div>
            </header>
            <main className="flex-1 overflow-y-auto bg-white h-0" ref={contentRef}>
              <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {stepContent}
              </div>
            </main>
          </div>
        </div>
      ) : (
        <div className="ios-viewport-fix flex min-h-screen flex-col bg-[#F5FAFF]">
          {/* Ultra-Minimal Fixed Progress Bar Only */}
          <div className="safe-area-top fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-[#166B93]/95 to-[#145A7A]/95 backdrop-blur-md shadow-md">
            <div className="px-4 py-2">
              {/* Minimal Progress Bar with Info */}
              <div className="flex items-center gap-3">
                <div className="flex-1 relative">
                  <div className="h-1 w-full bg-white/20 rounded-full overflow-hidden">
                    <div
                      className="mobile-progress-bar h-full bg-gradient-to-r from-[#CEF12F] to-[#E3FF7A] rounded-full shadow-sm"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                  {/* Step dots overlay */}
                  <div className="absolute -top-0.5 left-0 right-0 flex justify-between px-0.5">
                    {trackableSteps.map((step, index) => {
                      const hasCompleted = safeStepIndex > index;
                      const isActive = !hasCompleted && clampedStepIndex === index;
                      return (
                        <div
                          key={step.id}
                          className={`w-2 h-2 rounded-full transition-all duration-300 ${
                            hasCompleted
                              ? "bg-[#CEF12F] scale-110 shadow-sm shadow-[#CEF12F]/50"
                              : isActive
                                ? "bg-white scale-125 ring-2 ring-[#CEF12F] shadow-lg progress-dot-pulse"
                                : "bg-white/30"
                          }`}
                        />
                      );
                    })}
                  </div>
                </div>
                {/* Compact step counter */}
                <div className="flex items-center gap-1.5 text-[10px] font-bold flex-shrink-0">
                  <span className="text-white/90">
                    {Math.min(displayStepNumber, totalStepsToShow)}/{totalStepsToShow}
                  </span>
                  <span className="text-[#CEF12F]">
                    {progressPercent}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Main scrollable content */}
          <main
            className="safe-area-bottom flex-1 overflow-y-auto"
            ref={contentRef}
          >
            {/* Logo and Header Section - Scrollable */}
            <div className="bg-gradient-to-br from-[#166B93] via-[#166B93] to-[#145A7A] text-white pt-16 pb-6 px-4">
              <div className="max-w-lg mx-auto">
                {/* Logo */}
                <div className="flex justify-center mb-4">
                  <div className="bg-white backdrop-blur-sm rounded-2xl p-3 shadow-xl">
                    <img
                      src="/Icon_Jema.svg"
                      alt="Jema Services"
                      className="h-12 w-auto object-contain"
                    />
                  </div>
                </div>
                
                {/* Step Title */}
                <div className="text-center">
                  <h2 className="text-lg font-bold text-white mb-1">
                    {activeStepTitle}
                  </h2>
                  <p className="text-xs text-white/80">
                    {activeStepSubtitle}
                  </p>
                </div>
              </div>
            </div>

            {/* Content Card */}
            <div className="px-4 -mt-4 pb-32">
              <Card className="w-full border-0 bg-white shadow-2xl shadow-[#166B93]/20 rounded-2xl overflow-hidden">
                <CardContent className="p-6 sm:p-8 pb-24">{stepContent}</CardContent>
              </Card>
            </div>
          </main>
        </div>
      )}
    </div>
  );
}
